import { EquipmentStatusChip } from '@/equipments/presentation/components/EquipmentStatusChip';
import { SearchOutlined, ClearAll } from '@mui/icons-material';
import { Box, CircularProgress, InputAdornment, TextField, Typography, MenuItem, IconButton, SxProps, Theme, Button } from '@mui/material';
import { Close } from '@mui/icons-material';
import Select from '@/common/presentation/components/inputs/Select';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { SelectedEquipment } from '../RaptorJobCreateModal';
import tid from '@/common/application/utils/tid';
import { OptionType, useEquipmentSelector } from '../hook/useEquipmentSelector';

interface EquipmentSelectorProps {
  selectedClass: OptionType | null;
  setSelectedClass: (value: OptionType | null) => void;
  selectedType: OptionType | null;
  setSelectedType: (value: OptionType | null) => void;
  selectedSize: OptionType | null;
  setSelectedSize: (value: OptionType | null) => void;
  equipmentSearchTerm: string;
  setEquipmentSearchTerm: (term: string) => void;
  selectedEquipment: SelectedEquipment | null;
  setSelectedEquipment: (equipment: SelectedEquipment | null) => void;
  startDate?: Date | null;
  endDate?: Date | null;
  recurrence: 'single' | 'multi';
}

interface ClearableSelectProps {
  options: OptionType[];
  value: OptionType | null;
  onChange: (value: OptionType | null) => void;
  disabled?: boolean;
  label: string;
  placeholder: string;
  sx?: SxProps<Theme>;
  required?: boolean;
}

const ClearableSelect = ({ options, value, onChange, disabled, label, placeholder, sx, required, ...rest }: ClearableSelectProps) => {
  const isValueInOptions = value?.id && options.some((option) => option.id === value.id);
  const selectValue = isValueInOptions ? value.id : '';

  return (
    <Box sx={{ position: 'relative' }}>
      <Select
        fullWidth
        disabled={disabled}
        value={selectValue}
        onChange={(event) => {
          const selectedId = event.target.value;
          const selectedOption = options.find((option) => option.id === selectedId) || null;
          onChange(selectedOption);
        }}
        label={label}
        placeholder={placeholder}
        required={required}
        sx={sx}
        {...rest}
      >
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id || ''}>
            {option.name || ''}
          </MenuItem>
        ))}
      </Select>
      {value && (
        <IconButton
          size="small"
          onClick={() => onChange(null)}
          sx={{
            position: 'absolute',
            right: 32,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1
          }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export const EquipmentSelector = ({
  selectedClass,
  setSelectedClass,
  selectedType,
  setSelectedType,
  selectedSize,
  setSelectedSize,
  equipmentSearchTerm,
  setEquipmentSearchTerm,
  selectedEquipment,
  setSelectedEquipment,
  startDate,
  endDate,
  recurrence
}: EquipmentSelectorProps) => {
  const {
    filteredClasses,
    filteredTypes,
    filteredSizes,
    handleClassChange,
    handleTypeChange,
    handleSizeChange,
    handleEquipmentSelect,
    handleEquipmentDeselect,
    handleClearFilters,
    apiEquipments,
    hasActiveFilters,
    loadingEquipments,
    loadingEquipmentFormOptions
  } = useEquipmentSelector({
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    equipmentSearchTerm,
    setSelectedEquipment,
    recurrence,
    startDate,
    endDate
  });

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2
        }}
      >
        <ClearableSelect
          options={filteredClasses}
          value={selectedClass}
          onChange={handleClassChange}
          label="Equipment Class"
          placeholder="Equipment Class"
          {...tid('jobs:create:equipment-class-select')}
        />
        <ClearableSelect
          options={filteredTypes}
          value={selectedType}
          onChange={handleTypeChange}
          disabled={loadingEquipmentFormOptions || loadingEquipments}
          label="Equipment Type"
          placeholder="Equipment Type"
          {...tid('jobs:create:equipment-type-select')}
        />
        <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: 'auto' } }}>
          <ClearableSelect
            options={filteredSizes}
            value={selectedSize}
            onChange={handleSizeChange}
            disabled={loadingEquipmentFormOptions || loadingEquipments}
            label="Equipment Size"
            placeholder="Equipment Size"
            {...tid('jobs:create:equipment-size-select')}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="text"
          size="small"
          startIcon={<ClearAll />}
          onClick={handleClearFilters}
          disabled={!hasActiveFilters}
          sx={{ textTransform: 'none' }}
          {...tid('jobs:create:clear-filters-button')}
        >
          Clear Filters
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder="Search Equipment"
        value={equipmentSearchTerm}
        onChange={(e) => setEquipmentSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          )
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
            backgroundColor: 'background.paper',
            height: '40px'
          }
        }}
        {...tid('jobs:create:equipment-search-input')}
      />

      <Box sx={{ minHeight: '152px', maxHeight: '152px', overflowY: 'scroll' }}>
        {loadingEquipmentFormOptions ||
          (loadingEquipments && (
            <Box
              sx={{
                margin: '0 auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '152px'
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ))}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr', md: 'repeat(2, 1fr)' },
            gap: '4px',
            alignContent: 'start'
          }}
        >
          {!loadingEquipments &&
            (apiEquipments ?? []).map((equipment) => (
              <Box
                key={equipment.id}
                onClick={() => {
                  if (selectedEquipment && selectedEquipment.id === equipment.id) {
                    handleEquipmentDeselect();
                  } else {
                    handleEquipmentSelect({
                      id: equipment.id,
                      name: equipment.name,
                      class: equipment.class,
                      type: equipment.type,
                      size: equipment.size,
                      status: equipment.status
                    });
                  }
                }}
                sx={{
                  cursor: 'pointer',
                  maxHeight: '48px',
                  padding: 1,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: (theme) => (selectedEquipment?.id === equipment.id ? theme.palette.primary.main : theme.palette.divider),
                  backgroundColor: (theme) =>
                    selectedEquipment?.id === equipment.id ? `${theme.palette.primary.main}0A` : theme.palette.background.paper,
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', gap: '12px' }}>
                  <LocalShippingOutlinedIcon color="primary" />
                  <Typography component="p" fontWeight={600} fontSize="14px">
                    {equipment.name}
                  </Typography>
                </Box>
                {equipment.status?.name && <EquipmentStatusChip status={equipment.status.name} />}
              </Box>
            ))}
          {!loadingEquipments && (apiEquipments?.length ?? 0) === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ gridColumn: '1 / -1', p: 1 }}>
              No equipment found.
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};
